import React, { useState } from "react";
import { Plus } from "lucide-react";
import { Link } from "react-router-dom";

interface FAQItemProps {
  question: string;
  answer: React.ReactNode;
  isOpen: boolean;
  onClick: () => void;
}

const FAQItem: React.FC<FAQItemProps> = ({
  question,
  answer,
  isOpen,
  onClick,
}) => (
  <div className="relative w-full bg-white overflow-hidden border border-solid border-nileBlueLight mb-3 rounded-[15px] rounded-b-none">
    <button
      className="relative flex justify-between items-center text-left bg-white top-0 left-0 w-full outline-none text-black focus-visible:bg-shade100 p-5 [&_svg]:text-[#F47B20] [&_svg]:transition-transform [&_svg]:duration-150 [&_svg]:ml-4"
      onClick={onClick}
      aria-expanded={isOpen}
    >
      <span className="text-black leading-[1.56] text-[16px] md:text-[18px] font-normal w-[calc(100%-2rem)]">
        {question}
      </span>
      <Plus
        className={`transition-transform duration-150 ${
          isOpen ? "rotate-45" : ""
        }`}
      />
    </button>
    <div
      className={`transition-all duration-300 ease-in-out ${
        isOpen ? "max-h-[500px]" : "max-h-0"
      } overflow-hidden`}
    >
      <div className="px-5 pb-5 text-base leading-[1.56]">{answer}</div>
    </div>
  </div>
);

function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs = [
    {
      question: "Hoe vraag ik een offerte aan?",
      answer: (
        <>
          U kunt eenvoudig een offerte aanvragen via onze{" "}
          <Link
            to="/offerte"
            aria-label="Vraag een vrijblijvende offerte aan voor groepenkast vervanging"
            className="text-[#F47B20] underline"
          >
            offertepagina
          </Link>
          . Vul het formulier in en ontvang binnen 24 uur een vrijblijvende
          prijsopgave.
        </>
      ),
    },
    {
      question: "Wat houdt een elektra renovatie in?",
      answer: (
        <>
          Een elektra renovatie omvat het vervangen of upgraden van uw
          elektrische installatie om deze veiliger en efficiënter te maken. Meer
          informatie vindt u op onze{" "}
          <Link to="/elektra-renovatie" className="text-[#F47B20] underline">
            elektra renovatie pagina
          </Link>
          .
        </>
      ),
    },
    {
      question: "Wanneer moet ik mijn groepenkast vervangen?",
      answer: (
        <>
          Het vervangen van een groepenkast is nodig bij verouderde
          installaties, uitbreidingen zoals een kookgroep of laadpaal, of als u
          last heeft van stroomuitval en overbelasting. Bekijk onze dienst{" "}
          <Link
            to="/groepenkast-vervangen"
            className="text-[#F47B20] underline"
          >
            groepenkast vervangen
          </Link>
          .
        </>
      ),
    },
    {
      question: "Kan ik een kookgroep laten aansluiten?",
      answer: (
        <>
          Ja, wij kunnen een kookgroep aansluiten zodat uw elektrische kookplaat
          optimaal werkt. Lees meer op de pagina{" "}
          <Link to="/kookgroep-aansluiten" className="text-[#F47B20] underline">
            kookgroep aansluiten
          </Link>
          .
        </>
      ),
    },
    {
      question: "Wat zijn de voordelen van een 3-fase aansluiting?",
      answer: (
        <>
          Een 3-fase aansluiting is geschikt voor apparaten met een hoog
          vermogen, zoals een laadpaal of elektrische boiler. Lees er alles over
          op onze{" "}
          <Link to="/3-fase-aansluiting" className="text-[#F47B20] underline">
            3-fase aansluiting pagina
          </Link>
          .
        </>
      ),
    },
    {
      question: "Hoe werkt de installatie van een laadpaal?",
      answer: (
        <>
          Wij installeren laadpalen bij u thuis of op kantoor. Meer informatie
          vindt u op onze pagina{" "}
          <Link to="/laadpaal" className="text-[#F47B20] underline">
            laadpaal installatie
          </Link>
          .
        </>
      ),
    },
    {
      question: "Wat zijn de voordelen van zonnepanelen?",
      answer: (
        <>
          Zonnepanelen helpen u besparen op uw energierekening en dragen bij aan
          een duurzame toekomst. Bekijk onze service{" "}
          <Link to="/zonnepanelen" className="text-[#F47B20] underline">
            zonnepanelen installatie
          </Link>
          .
        </>
      ),
    },
    {
      question: "Bieden jullie klantenservice?",
      answer: (
        <>
          Ja, onze klantenservice staat klaar om al uw vragen te beantwoorden.
          Bezoek onze{" "}
          <Link to="/klantenservice" className="text-[#F47B20] underline">
            klantenservice pagina
          </Link>{" "}
          voor meer informatie.
        </>
      ),
    },
    {
      question: "Waar kan ik meer informatie vinden over vaktermen?",
      answer: (
        <>
          Op onze{" "}
          <Link to="/begrippen" className="text-[#F47B20] underline">
            begrippenpagina
          </Link>{" "}
          leggen wij alle vaktermen uit die te maken hebben met elektra en
          installaties.
        </>
      ),
    },
    {
      question: "Voeren jullie inspecties uit?",
      answer: (
        <>
          Ja, wij voeren elektrische inspecties uit om de veiligheid van uw
          installatie te controleren. Bekijk onze service{" "}
          <Link to="/inspectie" className="text-[#F47B20] underline">
            elektrische inspectie
          </Link>
          .
        </>
      ),
    },
    {
      question: "Hoe kan ik contact opnemen?",
      answer: (
        <>
          U kunt contact met ons opnemen via de{" "}
          <Link to="/contact" className="text-[#F47B20] underline">
            contactpagina
          </Link>
          . Wij zijn bereikbaar via telefoon, e-mail en ons contactformulier.
        </>
      ),
    },
  ];

  return (
    <section className="mx-auto w-[calc(100%-2.5rem)] max-w-[1260px] mb-12 md:mb-16">
      <div className="mx-auto md:max-w-[520px] lg:max-w-[100vw]">
        <div className="font-bold text-black leading-[1.25] text-[20px] lg:text-[28px] mb-6">
          Vraag en antwoord
        </div>
        <div className="grid auto-rows-auto lg:gap-x-8 lg:grid-cols-[1fr_1fr]">
          <div>
            {faqs.slice(0, Math.ceil(faqs.length / 2)).map((faq, index) => (
              <FAQItem
                key={index}
                question={faq.question}
                answer={faq.answer}
                isOpen={openIndex === index}
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
              />
            ))}
          </div>
          <div>
            {faqs.slice(Math.ceil(faqs.length / 2)).map((faq, index) => (
              <FAQItem
                key={index + Math.ceil(faqs.length / 2)}
                question={faq.question}
                answer={faq.answer}
                isOpen={openIndex === index + Math.ceil(faqs.length / 2)}
                onClick={() =>
                  setOpenIndex(
                    openIndex === index + Math.ceil(faqs.length / 2)
                      ? null
                      : index + Math.ceil(faqs.length / 2)
                  )
                }
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default FAQ;
